import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, Grid, InputLabel,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import EuroIcon from '@mui/icons-material/Euro';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { usePermissions } from '../contexts/PermissionsContext';
import { getSpaOperationalReport, getSpaFinancialReport } from '../APIs/SpaAPI';
import { reportPeriodRange, reportRangeError, spaReportsCsv } from '../utils/spaReports';

function ReportTable({ title, headers, rows, empty }) {
  return <TableContainer tabIndex={0} sx={{ maxWidth: '100%' }}>
    <Table size="small" aria-label={title} sx={{ minWidth: headers.length > 4 ? 560 : headers.length > 3 ? 460 : 280 }}>
      <TableHead><TableRow>{headers.map((header, index) =>
        <TableCell key={index} align={index ? 'right' : 'left'}>{header}</TableCell>)}</TableRow></TableHead>
      <TableBody>{rows.length ? rows.map((row, index) => <TableRow key={index}>
        {row.map((value, column) => <TableCell key={column} align={column ? 'right' : 'left'}
          sx={{ overflowWrap: column ? 'normal' : 'anywhere', whiteSpace: column ? 'nowrap' : 'normal' }}>{value}</TableCell>)}
      </TableRow>) : <TableRow><TableCell colSpan={headers.length}>{empty}</TableCell></TableRow>}</TableBody>
    </Table>
  </TableContainer>;
}

function ReportSection({ title, icon, children }) {
  return <Paper component="section" variant="outlined" sx={{ p: 2, height: '100%', minWidth: 0 }}>
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}<Typography component="h2" variant="h6" fontWeight={850}>{title}</Typography>
      </Stack>
      {children}
    </Stack>
  </Paper>;
}

function SpaReportsPage() {
  const { t, i18n } = useTranslation();
  const rt = (key, options) => t(`spa.reports.${key}`, options);
  const { can, loading: permissionsLoading } = usePermissions();
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const tenantType = String(localStorage.getItem('tenantType') || '').toUpperCase();
  const allowed = !permissionsLoading && can('spa-reports:view') && tenantType === 'SPA';
  const [period, setPeriod] = useState('today');
  const [range, setRange] = useState(() => reportPeriodRange('today'));
  const [groupBy, setGroupBy] = useState('treatment');
  const [reload, setReload] = useState(0);
  const [state, setState] = useState(null);
  const rangeError = reportRangeError(range);
  const key = `${tenant}:${token}:${range.from}:${range.to}:${allowed}`;
  const current = state?.key === key ? state : null;
  const operational = allowed && !rangeError && current?.operational?.data;
  const financial = allowed && !rangeError && current?.financial?.data;
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const number = (value) => value == null ? rt('unavailable') : new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
  const percent = (value) => value == null ? rt('unavailable') : `${number(value)}%`;
  const money = (value) => value == null ? rt('unavailable') : new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'EUR',
  }).format(value);
  const person = (item) => [item?.name, item?.surname].filter(Boolean).join(' ') || '-';
  const groupLabel = groupBy === 'customerType' ? rt('group.customerType') : t(`spa.fields.${groupBy}`);

  useEffect(() => {
    if (!allowed || !token || !tenant || rangeError) return undefined;
    const controller = new AbortController();
    setState({ key, operational: { loading: true }, financial: { loading: true } });
    const load = async (name, request) => {
      try {
        const data = await request({ from: range.from, to: range.to }, token, tenant, { signal: controller.signal });
        if (!controller.signal.aborted) setState((previous) => previous?.key === key
          ? { ...previous, [name]: { data } } : previous);
      } catch (error) {
        if (!controller.signal.aborted) setState((previous) => previous?.key === key
          ? { ...previous, [name]: { error: error.message, status: error.status } } : previous);
      }
    };
    load('operational', getSpaOperationalReport);
    load('financial', getSpaFinancialReport);
    return () => controller.abort();
  }, [allowed, token, tenant, range.from, range.to, rangeError, key, reload]);

  const changePeriod = (value) => {
    setPeriod(value);
    if (value !== 'custom') setRange(reportPeriodRange(value));
  };
  const exportReports = () => {
    if (!allowed || !operational || !financial) return;
    const url = URL.createObjectURL(new Blob([spaReportsCsv(operational, financial)], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `spa-reports-${range.from}-${range.to}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const table = (title, headers, rows) => <ReportTable title={title} headers={headers} rows={rows} empty={rt('empty')} />;
  const status = (name) => {
    const result = current?.[name];
    if (!result || result.loading) return <Stack direction="row" spacing={1} alignItems="center" role="status">
      <CircularProgress size={20} /><Typography>{t('spa.common.loading')}</Typography>
    </Stack>;
    if (result.error) return <Alert severity={result.status === 403 ? 'warning' : 'error'}
      action={<Button color="inherit" onClick={() => setReload((value) => value + 1)}>{rt('retry')}</Button>}>
      {result.status === 403 ? rt('denied') : rt('loadError')}
      {result.status && result.status !== 403 ? ` (HTTP ${result.status})` : ''}
    </Alert>;
    return null;
  };
  const revenueRows = financial?.revenue?.treatments?.[{
    treatment: 'byTreatment', operator: 'byOperator', cabin: 'byCabin', customerType: 'byCustomerType',
  }[groupBy]] || [];
  const revenueName = (row) => groupBy === 'operator' ? person(row.operator)
    : groupBy === 'cabin' ? row.cabin.name
      : groupBy === 'customerType' ? t(`spa.finance.enums.${row.customerType}`, { defaultValue: row.customerType }) : row.name;
  const kpis = [
    { key: 'treatments', value: operational && number(operational.treatmentCounts.reduce((sum, row) => sum + row.count, 0)), source: 'operational' },
    { key: 'occupancy', value: operational && percent(operational.occupancy.spa.occupancyPercent), source: 'operational' },
    { key: 'noShow', value: operational && percent(operational.outcomes.noShowPercent), source: 'operational' },
    { key: 'revenue', value: financial && money(financial.revenue.total.gross), source: 'financial' },
  ];

  return <SidebarLayout>
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320, mx: 'auto' }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box><Typography variant="h4" fontWeight={900}>{rt('title')}</Typography>
            <Typography color="text.secondary">{rt('subtitle')}</Typography></Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {operational && financial && <Chip color="info" label={t('spa.common.liveData')} />}
            <Button variant="outlined" startIcon={<RefreshIcon />} disabled={!allowed || Boolean(rangeError)}
              onClick={() => setReload((value) => value + 1)}>{t('spa.common.refresh')}</Button>
            <Button disabled={!operational || !financial} variant="outlined" startIcon={<DownloadIcon />}
              onClick={exportReports}>{rt('actions.export')}</Button>
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small"><InputLabel id="report-period">{rt('filters.period')}</InputLabel>
                <Select labelId="report-period" label={rt('filters.period')} value={period} onChange={(event) => changePeriod(event.target.value)}>
                  {['today', 'week', 'month', 'last30', 'custom'].map((value) => <MenuItem key={value} value={value}>{rt(`periods.${value}`)}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            {['from', 'to'].map((field) => <Grid item xs={12} sm={6} md={3} key={field}>
              <TextField fullWidth size="small" type="date" label={t(`spa.fields.${field}`)} value={range[field]}
                InputLabelProps={{ shrink: true }} error={Boolean(rangeError)} onChange={(event) => {
                  setPeriod('custom'); setRange((previous) => ({ ...previous, [field]: event.target.value }));
                }} />
            </Grid>)}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small"><InputLabel id="report-group">{rt('filters.revenueGroup')}</InputLabel>
                <Select labelId="report-group" label={rt('filters.revenueGroup')} value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                  {['treatment', 'operator', 'cabin', 'customerType'].map((value) => <MenuItem key={value} value={value}>
                    {value === 'customerType' ? rt('group.customerType') : t(`spa.fields.${value}`)}</MenuItem>)}
                </Select></FormControl>
            </Grid>
          </Grid>
        </Paper>

        {permissionsLoading ? <CircularProgress aria-label={t('spa.common.loading')} />
          : !allowed ? <Alert severity="warning">{rt('denied')}</Alert>
            : rangeError ? <Alert severity="warning">{rt(`validation.${rangeError}`)}</Alert>
              : <>
                <Grid container spacing={1.5} sx={{ ml: '-12px !important', width: 'calc(100% + 12px)' }}>
                  {kpis.map((item) => <Grid item xs={12} sm={6} md={3} key={item.key}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%', minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={800}>{rt(`kpis.${item.key}`)}</Typography>
                      <Typography variant="h4" fontWeight={900} sx={{ fontSize: 28, overflowWrap: 'anywhere' }} data-testid={`report-${item.key}`}>
                        {item.value || (current?.[item.source]?.error ? rt('unavailable') : '...')}
                      </Typography>
                    </Paper>
                  </Grid>)}
                </Grid>

                <Grid container spacing={2} sx={{ ml: '-16px !important', width: 'calc(100% + 16px)' }}>
                  <Grid item xs={12} lg={7}><ReportSection title={rt('operational.title')} icon={<AssessmentIcon color="primary" />}>
                    {status('operational') || <>
                      <Typography variant="body2" color="text.secondary">{rt('operationalBasis')}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`${rt('appointments')}: ${number(operational.outcomes.appointments)}`} />
                        <Chip label={`${rt('cancellations')}: ${number(operational.outcomes.cancellations)} (${percent(operational.outcomes.cancellationPercent)})`} />
                        <Chip label={`${rt('columns.noShow')}: ${number(operational.outcomes.noShows)} (${percent(operational.outcomes.noShowPercent)})`} />
                      </Stack>
                      {table(rt('treatmentCounts'), [t('spa.fields.treatment'), rt('columns.qty')],
                        operational.treatmentCounts.map((row) => [row.name, number(row.count)]))}
                    </>}
                  </ReportSection></Grid>
                  <Grid item xs={12} lg={5}><ReportSection title={rt('economic.title')} icon={<EuroIcon color="success" />}>
                    {status('financial') || <>
                      <Typography variant="body2" color="text.secondary">{rt('financialBasis')}</Typography>
                      {table(rt('revenueTotals'), [rt('category'), rt('columns.net'), rt('vat'), rt('columns.gross')],
                        ['treatments', 'retail', 'total'].map((name) => {
                          const value = name === 'total' ? financial.revenue.total : financial.revenue[name].total;
                          return [rt(name === 'total' ? 'total' : `categories.${name}`), money(value.net), money(value.vat), money(value.gross)];
                        }))}
                      <Typography variant="body2" data-testid="report-average-booking">{rt('kpis.averageBooking')}: {money(financial.revenue.bookings?.averageGross)}</Typography>
                      <Typography variant="body2" data-testid="report-average-treatment">{rt('averageTreatment')}: {money(financial.revenue.treatments.averageGross)}</Typography>
                      <Typography variant="body2">{rt('retailOrders')}: {number(financial.revenue.retail.orderCount)} | {rt('retailQuantity')}: {number(financial.revenue.retail.itemQuantity)}</Typography>
                      <Typography variant="body2">{rt('averageRetail')}: {money(financial.revenue.retail.averageOrderGross)}</Typography>
                    </>}
                  </ReportSection></Grid>
                </Grid>

                {operational && <>
                  <Grid container spacing={2} sx={{ ml: '-16px !important', width: 'calc(100% + 16px)' }}>
                    {['operator', 'cabin'].map((resource) => <Grid item xs={12} lg={6} key={resource}>
                      <ReportSection title={rt(`${resource}Utilization`)} icon={<QueryStatsIcon color="primary" />}>
                        {table(rt(`${resource}Utilization`), [t(`spa.fields.${resource}`), rt('columns.qty'), rt('busyMinutes'), rt('availableMinutes'), rt('utilization')],
                          operational[`${resource}Utilization`].map((row) => [resource === 'operator' ? person(row.operator) : row.cabin.name,
                            number(row.treatmentCount), number(row.busyMinutes), number(row.availableMinutes), percent(row.utilizationPercent)]))}
                      </ReportSection>
                    </Grid>)}
                  </Grid>
                  <ReportSection title={rt('capacity')} icon={<QueryStatsIcon color="primary" />}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`${rt('kpis.availableCapacity')}: ${number(operational.capacity.total)}`} />
                      <Chip label={`${rt('perInterval')}: ${number(operational.capacity.perInterval)}`} />
                      <Chip label={`${rt('openingMinutes')}: ${number(operational.capacity.openingMinutes)}`} />
                      <Chip label={`${rt('interval')}: ${number(operational.capacity.plannerIntervalMinutes)}`} />
                    </Stack>
                    {table(rt('areaOccupancy'), [t('spa.fields.area'), rt('usedPersonMinutes'), rt('capacityPersonMinutes'), rt('columns.occupancy')],
                      [[rt('wholeSpa'), number(operational.occupancy.spa.usedPersonMinutes), number(operational.occupancy.spa.capacityPersonMinutes), percent(operational.occupancy.spa.occupancyPercent)],
                        ...operational.occupancy.areas.map((row) => [row.area.name, number(row.usedPersonMinutes), number(row.capacityPersonMinutes), percent(row.occupancyPercent)])])}
                  </ReportSection>
                </>}

                {financial && <>
                  <ReportSection title={rt('revenueBreakdown')} icon={<EuroIcon color="success" />}>
                    {groupBy === 'cabin' && <Typography variant="body2" color="text.secondary">{rt('cabinAttribution')}</Typography>}
                    {table(rt('revenueBreakdown'), [groupLabel, rt('columns.qty'), rt('columns.net'), rt('vat'), rt('columns.gross')],
                      revenueRows.map((row) => [revenueName(row), number(row.count), money(row.net), money(row.vat), money(row.gross)]))}
                  </ReportSection>
                  <Grid container spacing={2} sx={{ ml: '-16px !important', width: 'calc(100% + 16px)' }}>
                    <Grid item xs={12} lg={6}><ReportSection title={rt('costsAndMargin')} icon={<EuroIcon color="success" />}>
                      {table(rt('costsAndMargin'), [rt('category'), rt('productCosts'), rt('commissions')],
                        ['treatments', 'retail', 'total'].map((name) => [rt(name === 'total' ? 'total' : `categories.${name}`), money(financial.costs[name]), money(financial.commissions[name])]))}
                      <Typography fontWeight={800}>{rt('columns.margin')}: {money(financial.margin.amount)} ({percent(financial.margin.percent)})</Typography>
                    </ReportSection></Grid>
                    <Grid item xs={12} lg={6}><ReportSection title={rt('retailBreakdown')} icon={<AssessmentIcon color="primary" />}>
                      {table(rt('retailBreakdown'), [rt('product'), rt('columns.qty'), rt('columns.net'), rt('vat'), rt('columns.gross')],
                        financial.revenue.retail.byProduct.map((row) => [row.name, number(row.quantity), money(row.net), money(row.vat), money(row.gross)]))}
                    </ReportSection></Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ ml: '-16px !important', width: 'calc(100% + 16px)' }}>
                    <Grid item xs={12} lg={6}><ReportSection title={rt('packageUsage')} icon={<AssessmentIcon color="primary" />}>
                      {table(rt('packageUsage'), [rt('categories.packages'), rt('consumed'), rt('reversed'), rt('netUsage')],
                        financial.usage.packages.map((row) => [`${row.packageName} #${row.entitlementId}`, number(row.consumedQuantity), number(row.reversedQuantity), number(row.netQuantity)]))}
                    </ReportSection></Grid>
                    <Grid item xs={12} lg={6}><ReportSection title={rt('storedValueUsage')} icon={<EuroIcon color="success" />}>
                      {table(rt('storedValueUsage'), [rt('category'), rt('consumed'), rt('reversed'), rt('netUsage')],
                        financial.usage.storedValue.map((row) => [t(`spa.finance.enums.${row.type}`, { defaultValue: row.type }), money(row.consumedAmount), money(row.reversedAmount), money(row.netAmount)]))}
                    </ReportSection></Grid>
                  </Grid>
                </>}
              </>}
      </Stack>
    </Box>
  </SidebarLayout>;
}

export default SpaReportsPage;
